import type { HttpError } from '@packages/domain/constants/http';
import {
  trackResendOTPCode,
  trackVerifyOTPSuccessful,
  trackVerifyOTPWrong,
  trackViewOTPModal,
} from 'actions/tracker/tracker';
import { newNotification } from 'actions/ui/ui';
import { AUTOFOCUS_DELAY_MS } from 'constants/timers';
import { useSendOtpPayload } from 'hooks/useSendOtpPayload/useSendOtpPayload';
import { useCallback, useEffect, useRef, useState } from 'react';
import { otpSendCache } from 'services/payex/otp/otpSendCache';
import type { OtpContext } from 'services/payex/otp/types';
import { OtpChannel } from 'services/payex/otp/types';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils/translations/useTranslations';
import { useCountdown } from './useCountdown';
import { useSendOtp } from './useSendOtp';
import { useVerifyOtp } from './useVerifyOtp';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const FOCUS_MAX_ATTEMPTS = 5;

export const resetOtpCache = otpSendCache.reset;

type OtpApiError = {
  type: string;
  message: string;
  attemptsRemaining?: number;
};

const parseOtpError = (error: HttpError): OtpApiError | null => {
  const errors = error.parsedBody?.errors as OtpApiError[] | undefined;
  return errors?.[0] ?? null;
};

type UseOtpFlowParams = {
  isOpen: boolean;
  email: string;
  phone?: string;
  senderName?: string;
  recipientName?: string;
  context: OtpContext;
  channel: OtpChannel;
  onVerified: () => void;
  onSendError?: () => void;
};

const useOtpFlow = ({
  isOpen,
  email,
  phone,
  senderName,
  recipientName,
  context,
  channel,
  onVerified,
  onSendError,
}: UseOtpFlowParams) => {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const sendOtp = useSendOtp();
  const verifyOtp = useVerifyOtp();
  const buildPayload = useSendOtpPayload();

  const pinFieldRef = useRef<HTMLDivElement>(null);
  const lastFailedCodeRef = useRef<string | null>(null);

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { countdown: resendCooldown, start: startResendCooldown } =
    useCountdown();

  const isLoading = sendOtp.isPending || verifyOtp.isPending;

  const buildSendOtpPayload = useCallback(
    () =>
      buildPayload({
        email,
        phone,
        senderName,
        recipientName,
        context,
        channel,
      }),
    [buildPayload, email, phone, senderName, recipientName, context, channel],
  );

  const focusFirstInput = useCallback(() => {
    const attemptFocus = (retriesLeft = FOCUS_MAX_ATTEMPTS) => {
      const firstInput =
        pinFieldRef.current?.querySelector<HTMLInputElement>('input');

      if (firstInput && document.activeElement !== firstInput) {
        firstInput.focus();

        if (document.activeElement !== firstInput && retriesLeft > 0) {
          setTimeout(() => attemptFocus(retriesLeft - 1), 100);
        }
      }
    };

    setTimeout(() => attemptFocus(), AUTOFOCUS_DELAY_MS);
  }, []);

  const resetState = useCallback(() => {
    setCode('');
    setErrorMessage('');
  }, []);

  const showSendFailedToast = useCallback(() => {
    dispatch(
      newNotification({
        message:
          channel === OtpChannel.Sms
            ? 'otp.sms.error.sendFailedToast'
            : 'otp.error.sendFailed',
        type: 'error',
      }),
    );
  }, [dispatch, channel]);

  const showVerifyFailedToast = useCallback(() => {
    dispatch(
      newNotification({
        message: 'otp.error.verifyFailed',
        type: 'error',
      }),
    );
  }, [dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only fire when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(trackViewOTPModal());
      resetState();
      lastFailedCodeRef.current = null;
      focusFirstInput();
    }
  }, [isOpen]);

  const handleVerify = () => {
    if (code.length !== OTP_LENGTH) {
      setErrorMessage(i18n.t('otp.error.required'));
      return;
    }

    setErrorMessage('');
    const recipient = channel === OtpChannel.Sms ? (phone ?? '') : email;
    verifyOtp.mutate(
      { recipient, code },
      {
        onSuccess: () => {
          dispatch(trackVerifyOTPSuccessful(channel));
          onVerified();
        },
        onError: (error: HttpError) => {
          const otpError = parseOtpError(error);
          if (!otpError) {
            showVerifyFailedToast();
            setCode('');
            focusFirstInput();
            return;
          }

          if (code !== lastFailedCodeRef.current) {
            dispatch(trackVerifyOTPWrong(channel));
            lastFailedCodeRef.current = code;
          }
          setErrorMessage(i18n.t('otp.error.invalid'));
          setCode('');
          focusFirstInput();
        },
      },
    );
  };

  const handleResend = () => {
    if (resendCooldown > 0) {
      return;
    }

    lastFailedCodeRef.current = null;
    resetState();
    startResendCooldown(RESEND_COOLDOWN_SECONDS);

    sendOtp.mutate(buildSendOtpPayload(), {
      onSuccess: () => {
        dispatch(trackResendOTPCode(channel));
        otpSendCache.record({ email, phone, context, channel });
      },
      onError: (error: HttpError) => {
        if (error.status === 429) {
          dispatch(
            newNotification({
              message: 'otp.error.rateLimitExceeded',
              type: 'error',
            }),
          );
        }
        if (onSendError) {
          onSendError();
        } else {
          showSendFailedToast();
        }
      },
    });
  };

  return {
    code,
    setCode,
    errorMessage,
    isLoading,
    resendCooldown,
    pinFieldRef,
    handleVerify,
    handleResend,
  };
};

export { useOtpFlow };
